<script setup lang="ts">
/**
 * The team availability calendar: an endless strip of real days, with one block
 * per thing someone has said about their own time.
 *
 * It scrolls sideways rather than paging a fixed window, because a week is not
 * a unit anybody actually thinks in when the question is "when are we all
 * about" — the answer straddles whatever boundary a pager would draw. Days are
 * added at whichever end the reader is heading toward and their entries are
 * fetched as they arrive, so the strip has no ends worth speaking of.
 *
 * Why this is not the gantt with dates turned on: the gantt answers "how is the
 * work laid out", relative to now, and deliberately has no calendar boundaries
 * in it. This answers "who is actually about on Thursday", which only makes
 * sense against a real week — and the two must be able to disagree, because a
 * bar scheduled across a day someone is away is precisely the thing worth
 * seeing.
 *
 * Everything below is in the VIEWER's timezone; the wire carries unix ms. The
 * geometry (clipping to a day, packing overlaps into lanes) lives in
 * shared/availability.ts so it can be tested without a DOM.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useBoard } from "../stores/board.js";
import { useViewport } from "../stores/viewport.js";
import ClaudeMark from "./ClaudeMark.vue";
import { toast } from "../lib/toast.js";
import {
  addDays,
  dayLabel,
  days,
  fromLocalInput,
  hourLabel,
  isToday,
  isWeekend,
  spanLabel,
  startOfDay,
  timeLabel,
  toLocalInput,
  weekdayLabel,
  windowLabel,
} from "../lib/calendar.js";
import {
  AVAILABILITY_LABELS,
  availabilityProblem,
  claudePrimeAt,
  clip,
  coversWholeDay,
  dayFraction,
  dayFractions,
  entriesInWindow,
  packLanes,
  type Span,
} from "@shared/availability.js";
import {
  AVAILABILITY_KINDS,
  AVAILABILITY_NOTE_MAX,
  HOUR_MS,
  snapToHour,
  type Availability,
  type AvailabilityKind,
} from "@shared/types.js";

const board = useBoard();
const viewport = useViewport();

/** Hours down the left. The whole day, scrolled to the working part on open. */
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const SCROLL_TO_HOUR = 7;

/** Roughly how many days to fit on screen, and what one may shrink or grow to. */
const TARGET_COLS = 7;
const COL_MIN = 116;
const COL_MAX = 260;
/** Days rendered at rest, and how many are added each time an end comes near. */
const LEAD_DAYS = 14;
const INITIAL_DAYS = 42;
const PAGE_DAYS = 14;
/** How close to an end counts as "heading that way". */
const EDGE_COLS = 4;
/**
 * Days of context left of today when jumping to it. The gantt's own "now"
 * button leaves about 15% of the view behind the line for the same reason: a
 * date pinned to the very edge has nothing before it to read it against.
 */
const JUMP_LEAD = 1;
/**
 * Where growth stops. Not a performance limit — a column is one element — but
 * the fetched window widens with the strip, and the server refuses a window
 * wider than two years. Stopping well short means that refusal is unreachable.
 */
const MAX_DAYS = 600;

const scroller = ref<HTMLElement | null>(null);
const colWidth = ref(160);
/** Read off the rendered gutter rather than repeated from the stylesheet, which
 *  narrows it on a phone; a copy here would be wrong on exactly one screen size. */
const gutterWidth = ref(62);

// ── the strip ─────────────────────────────────────────────────────────────
/** The first day rendered, and how many follow it. A count rather than an end
 *  date, so growing the strip never has to do arithmetic across a DST boundary. */
const rangeStart = ref(addDays(startOfDay(Date.now()), -LEAD_DAYS));
const dayCount = ref(INITIAL_DAYS);

const dayList = computed(() => days(rangeStart.value, dayCount.value));

/** Everything rendered. What gets fetched, and what the columns draw from. */
const windowSpan = computed((): Span => ({
  startMs: dayList.value[0]!.startMs,
  endMs: dayList.value.at(-1)!.endMs,
}));

/** Just the days on screen. What the heading names, so the header bar is always
 *  talking about what you are looking at. */
const visibleSpan = ref<Span>({ startMs: rangeStart.value, endMs: addDays(rangeStart.value, TARGET_COLS) });

/** Everyone who could appear, for the filter: the team on screen, in name order. */
const people = computed(() => board.teamMembers);

const shown = computed(() =>
  entriesInWindow(board.availability.values(), windowSpan.value).filter((e) => !viewport.calendarHidden.has(e.userId)),
);

/** One entry, cut down to the part of it that falls inside one day. */
interface DayBlock extends Span {
  entry: Availability;
}

/** Where a flagged entry's priming prompt goes out, on the day it falls on. */
interface PrimeLine {
  key: string;
  style: { top: string };
}

/**
 * Each day's blocks, split into the all-day band and the hour grid.
 *
 * A span that swallows a whole day goes in the band: a week away is one fact,
 * and five full-height columns of it would bury every meeting underneath.
 */
const columns = computed(() =>
  dayList.value.map((day) => {
    const allDay: Availability[] = [];
    const inside: DayBlock[] = [];
    const primes: PrimeLine[] = [];
    for (const entry of shown.value) {
      // Drawn against the DAY, not the block: it always falls before the entry
      // starts, and can land on the day before.
      const top = entry.needsClaude ? dayFraction(claudePrimeAt(entry), day) : null;
      if (top !== null) primes.push({ key: entry.id, style: { top: pct(top) } });
      if (coversWholeDay(entry, day)) {
        allDay.push(entry);
        continue;
      }
      const part = clip(entry, day);
      if (part) inside.push({ ...part, entry });
    }
    return { day, allDay, blocks: packLanes(inside), primes };
  }),
);

/** The clock, ticked each minute, so the "now" line creeps down a column left open. */
const now = ref(Date.now());
const clock = setInterval(() => (now.value = Date.now()), 60_000);

/** Where the "now" line crosses the column for `day`, or null on every other day. */
const nowLine = (day: Span): { top: string } | null => {
  const top = dayFraction(now.value, day);
  return top === null ? null : { top: pct(top) };
};

/** Who is away right now, for the line under the heading. */
const awayNow = computed(() => {
  const now = Date.now();
  const names = new Set<string>();
  for (const entry of board.availability.values()) {
    if (entry.kind === "away" && entry.startMs <= now && entry.endMs > now) names.add(board.userName(entry.userId));
  }
  return [...names].sort();
});

// ── loading ───────────────────────────────────────────────────────────────
/**
 * Fetch whatever is rendered, plus a week either side so the columns just off
 * each edge are already populated when they scroll in. The store widens what it
 * holds rather than replacing it, so a strip that has grown five times has been
 * fetched five times, not twenty-five.
 */
async function ensureLoaded(): Promise<void> {
  const { startMs, endMs } = windowSpan.value;
  try {
    await board.loadAvailability(addDays(startMs, -7), addDays(endMs, 7));
  } catch (err) {
    toast(`Could not load the calendar: ${(err as Error).message}`);
  }
}

watch(windowSpan, () => void ensureLoaded());

// ── growing the strip ─────────────────────────────────────────────────────
/** Set while a grow is correcting scrollLeft, so the scroll it causes is ignored. */
let adjusting = false;

/**
 * Add days before the first one rendered.
 *
 * The new columns land to the LEFT of everything on screen, which shifts the
 * content the reader is looking at to the right by exactly their width — so
 * scrollLeft is put back by the same amount and nothing appears to move.
 */
async function prepend(): Promise<void> {
  const el = scroller.value;
  if (!el || dayCount.value >= MAX_DAYS) return;
  adjusting = true;
  rangeStart.value = addDays(rangeStart.value, -PAGE_DAYS);
  dayCount.value += PAGE_DAYS;
  await nextTick();
  el.scrollLeft += PAGE_DAYS * colWidth.value;
  adjusting = false;
}

/** Add days after the last one rendered. Nothing on screen moves, so nothing to fix. */
async function append(): Promise<void> {
  if (dayCount.value >= MAX_DAYS) return;
  dayCount.value += PAGE_DAYS;
  await nextTick();
}

/** Which days are on screen, for the heading. */
function readVisible(): void {
  const el = scroller.value;
  if (!el) return;
  const list = dayList.value;
  const first = Math.min(list.length - 1, Math.max(0, Math.floor(el.scrollLeft / colWidth.value)));
  const across = Math.max(1, Math.round((el.clientWidth - gutterWidth.value) / colWidth.value));
  const last = Math.min(list.length - 1, first + across - 1);
  visibleSpan.value = { startMs: list[first]!.startMs, endMs: list[last]!.endMs };
}

function onScroll(): void {
  if (adjusting) return;
  readVisible();
  const el = scroller.value!;
  const edge = colWidth.value * EDGE_COLS;
  if (el.scrollLeft < edge) void prepend();
  else if (el.scrollLeft + el.clientWidth > el.scrollWidth - edge) void append();
}

/** Put `day` at the left edge, rebuilding the strip around it when it is nowhere near. */
async function goToDay(ms: number, smooth: boolean): Promise<void> {
  const day = startOfDay(ms);
  const list = dayList.value;
  if (day < list[0]!.startMs || day >= list.at(-1)!.endMs) {
    adjusting = true;
    rangeStart.value = addDays(day, -LEAD_DAYS);
    dayCount.value = INITIAL_DAYS;
    await nextTick();
    adjusting = false;
    smooth = false;
  }
  const index = dayList.value.findIndex((d) => d.startMs === day);
  if (index < 0) return;
  scroller.value?.scrollTo({ left: index * colWidth.value, behavior: smooth ? "smooth" : "auto" });
}

/** Today, with a day of yesterday showing so it does not sit on the very edge. */
const goToToday = (): Promise<void> => goToDay(addDays(startOfDay(Date.now()), -JUMP_LEAD), true);

/**
 * Step sideways a week at a time. Room is made FIRST: a smooth scroll cannot go
 * past the scrollWidth as it stands when it starts, so stepping off the end
 * without growing first would stop short of a week.
 */
async function step(byDays: number): Promise<void> {
  if (byDays > 0) await append();
  else await prepend();
  scroller.value?.scrollBy({ left: byDays * colWidth.value, behavior: "smooth" });
}

/** A column wide enough to read a name in, sized so about a week fits. */
function measure(): void {
  const el = scroller.value;
  if (!el) return;
  const gutter = el.querySelector<HTMLElement>(".cal-corner");
  if (gutter?.offsetWidth) gutterWidth.value = gutter.offsetWidth;
  colWidth.value = Math.round(
    Math.min(COL_MAX, Math.max(COL_MIN, (el.clientWidth - gutterWidth.value) / TARGET_COLS)),
  );
}

let observer: ResizeObserver | null = null;

onMounted(async () => {
  void ensureLoaded();
  // Always today, never where the reader last left it: the calendar answers
  // "who is about", and the day that question is asked about is nearly always
  // this one. `Today` and the arrows are how you go anywhere else.
  const opensOn = addDays(startOfDay(Date.now()), -JUMP_LEAD);
  measure();
  // The columns are still the width they were rendered at; scrolling to a
  // day by index before that lands somewhere else entirely.
  await nextTick();
  await goToDay(opensOn, false);
  readVisible();
  // Open on the working day rather than on midnight, which is the one hour
  // nobody is coordinating around.
  // Measured off a real column rather than the scroll height, which includes
  // the sticky heads and would land an hour or so out.
  const el = scroller.value;
  const column = el?.querySelector<HTMLElement>(".cal-col");
  if (el && column) el.scrollTop = (column.offsetHeight * SCROLL_TO_HOUR) / 24;
  // A resize changes the column width, so both the strip's geometry and the
  // span the heading names have to be worked out again.
  observer = new ResizeObserver(() => {
    measure();
    readVisible();
  });
  if (el) observer.observe(el);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  clearInterval(clock);
});

// ── geometry ──────────────────────────────────────────────────────────────
const pct = (n: number): string => `${n * 100}%`;

function boxStyle(block: { item: DayBlock; lane: number; lanes: number }, day: Span): Record<string, string> {
  const f = dayFractions(block.item, day)!;
  return {
    top: pct(f.top),
    height: pct(f.height),
    left: pct(block.lane / block.lanes),
    width: pct(1 / block.lanes),
    "--cal-color": board.userColor(block.item.entry.userId),
  };
}

/**
 * The instant a pointer at `clientY` is over, snapped to the hour.
 *
 * Reads the column's own box and maps proportionally across the day's REAL
 * length, which is the same mapping dayFractions() draws with — so the hour a
 * drag lands on is the hour it looked like it would land on, including on the
 * two days a year the clocks move.
 */
function instantAt(el: HTMLElement, clientY: number, day: Span): number {
  const box = el.getBoundingClientRect();
  const fraction = Math.min(1, Math.max(0, (clientY - box.top) / box.height));
  return snapToHour(day.startMs + fraction * (day.endMs - day.startMs));
}

// ── drag to create ────────────────────────────────────────────────────────
/** The span being dragged out right now, drawn as a ghost until the pointer lifts. */
const draft = ref<{ day: Span; startMs: number; endMs: number } | null>(null);

const draftStyle = computed((): Record<string, string> | null => {
  if (!draft.value) return null;
  const { day, startMs, endMs } = draft.value;
  const f = dayFractions({ startMs, endMs }, day);
  return f ? { top: pct(f.top), height: pct(f.height) } : null;
});

function startDrag(ev: PointerEvent, day: Span): void {
  // Only a plain drag on the empty grid; a click that landed on a block is
  // that block's to handle.
  if (ev.button !== 0 || (ev.target as HTMLElement).closest(".cal-block")) return;
  const col = ev.currentTarget as HTMLElement;
  const anchor = instantAt(col, ev.clientY, day);
  draft.value = { day, startMs: anchor, endMs: anchor + HOUR_MS };

  const move = (e: PointerEvent): void => {
    const at = instantAt(col, e.clientY, day);
    // Dragging upward is just as natural as downward; the anchor is whichever
    // end the pointer is not on.
    draft.value = at < anchor
      ? { day, startMs: at, endMs: anchor + HOUR_MS }
      : { day, startMs: anchor, endMs: at + HOUR_MS };
  };
  /**
   * `commit` separates a finished drag from an abandoned one. A cancel is NOT a
   * quiet pointerup: the browser fires one when it takes the gesture over to
   * scroll, which on a touch screen is most of them — treating that as a drop
   * would pop the editor open every time someone scrolled the calendar.
   */
  const finish = (commit: boolean): void => {
    col.removeEventListener("pointermove", move);
    col.removeEventListener("pointerup", drop);
    col.removeEventListener("pointercancel", abandon);
    const span = draft.value;
    draft.value = null;
    if (commit && span) openNew(span.startMs, span.endMs);
  };
  const drop = (): void => finish(true);
  const abandon = (): void => finish(false);

  col.addEventListener("pointermove", move);
  col.addEventListener("pointerup", drop);
  col.addEventListener("pointercancel", abandon);

  // Capture LAST, and never fatally: it throws for a pointer the element does
  // not own, and doing it before the listeners were attached would strand the
  // ghost block on screen with nothing left able to clear it.
  try {
    col.setPointerCapture(ev.pointerId);
  } catch {
    // Without capture the drag still works; it just stops tracking if the
    // pointer leaves the column.
  }
  ev.preventDefault();
}

// ── the entry editor ──────────────────────────────────────────────────────
interface Draft {
  id: string | null;
  userId: string;
  kind: AvailabilityKind;
  from: string;
  to: string;
  note: string;
  needsClaude: boolean;
}

const editing = ref<Draft | null>(null);
const saving = ref(false);
const serverError = ref<string | null>(null);

/** Why the form cannot be submitted yet, or null. The server's own rule, run here first. */
const blocker = computed((): string | null => {
  const d = editing.value;
  if (!d) return null;
  return availabilityProblem({
    kind: d.kind,
    startMs: fromLocalInput(d.from),
    endMs: fromLocalInput(d.to),
    note: d.note,
    needsClaude: d.needsClaude,
  });
});

/** Editing someone else's entry is read-only unless you are an admin. */
const readOnly = computed(() => !!editing.value && !board.mayEditCalendar(editing.value.userId));

function openNew(startMs: number, endMs: number): void {
  serverError.value = null;
  editing.value = {
    id: null,
    userId: board.me!.id,
    kind: "away",
    from: toLocalInput(startMs),
    to: toLocalInput(endMs),
    note: "",
    needsClaude: false,
  };
}

function openExisting(entry: Availability): void {
  serverError.value = null;
  editing.value = {
    id: entry.id,
    userId: entry.userId,
    kind: entry.kind,
    from: toLocalInput(entry.startMs),
    to: toLocalInput(entry.endMs),
    note: entry.note,
    needsClaude: entry.needsClaude,
  };
}

async function save(): Promise<void> {
  const d = editing.value;
  if (!d || blocker.value || saving.value) return;
  saving.value = true;
  serverError.value = null;
  const input = {
    kind: d.kind,
    startMs: fromLocalInput(d.from),
    endMs: fromLocalInput(d.to),
    note: d.note.trim(),
    needsClaude: d.needsClaude,
  };
  try {
    if (d.id) await board.patchAvailability(d.id, input);
    else await board.addAvailability({ ...input, userId: d.userId });
    editing.value = null;
  } catch (err) {
    // Keep the form open with what was typed: a rejected save must not cost
    // someone the dates they just picked.
    serverError.value = (err as Error).message;
  } finally {
    saving.value = false;
  }
}

async function remove(): Promise<void> {
  const d = editing.value;
  if (!d?.id || saving.value) return;
  saving.value = true;
  try {
    await board.removeAvailability(d.id);
    editing.value = null;
  } catch (err) {
    serverError.value = (err as Error).message;
  } finally {
    saving.value = false;
  }
}

function onKeydown(ev: KeyboardEvent): void {
  if (ev.key === "Escape") editing.value = null;
  if (ev.key === "Enter" && (ev.metaKey || ev.ctrlKey)) void save();
}

/** The one-line summary on a block and in its tooltip. */
const summary = (entry: Availability): string =>
  [
    board.userName(entry.userId),
    AVAILABILITY_LABELS[entry.kind].label.toLowerCase(),
    entry.note,
    entry.needsClaude ? "need Claude" : "",
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * Being around and needing Claude is not a thing you can be, so picking `free`
 * drops the flag rather than leaving a save the server would refuse.
 */
function pickKind(kind: AvailabilityKind): void {
  if (!editing.value) return;
  editing.value.kind = kind;
  if (kind === "free") editing.value.needsClaude = false;
}
</script>

<template>
  <section class="calendar" aria-label="Team availability">
    <header class="cal-head">
      <div class="cal-nav">
        <button class="btn ghost" type="button" title="Back a week" @click="step(-7)">‹</button>
        <button class="btn ghost" type="button" @click="goToToday">Today</button>
        <button class="btn ghost" type="button" title="On a week" @click="step(7)">›</button>
      </div>

      <strong class="cal-window">{{ windowLabel(visibleSpan.startMs, visibleSpan.endMs) }}</strong>

      <span class="spacer"></span>

      <p v-if="awayNow.length" class="cal-away-now" :title="`Away right now: ${awayNow.join(', ')}`">
        Away now: {{ awayNow.join(", ") }}
      </p>

      <!-- Hidden rather than shown, so a new teammate appears for everyone. -->
      <div class="avatars cal-filter">
        <button
          v-for="p in people"
          :key="p.id"
          type="button"
          class="avatar cal-filter-avatar"
          :class="{ off: viewport.calendarHidden.has(p.id) }"
          :style="{ background: p.color }"
          :aria-pressed="!viewport.calendarHidden.has(p.id)"
          :title="`${p.displayName} — click to ${viewport.calendarHidden.has(p.id) ? 'show' : 'hide'}`"
          @click="viewport.togglePerson(p.id)"
        >{{ p.displayName.slice(0, 2) }}</button>
      </div>
    </header>

    <div ref="scroller" class="cal-scroll" @scroll.passive="onScroll">
      <div class="cal-grid" :style="{ '--cal-cols': dayList.length, '--cal-col-w': colWidth + 'px' }">
        <div class="cal-corner"></div>
        <div
          v-for="d in dayList"
          :key="`h${d.startMs}`"
          class="cal-dayhead"
          :data-day="d.startMs"
          :class="{ today: isToday(d.startMs), weekend: isWeekend(d.startMs) }"
        >
          <span class="cal-dayname">{{ weekdayLabel(d.startMs) }}</span>
          <span class="cal-daynum">{{ dayLabel(d.startMs) }}</span>
        </div>

        <!-- All-day band: spans that swallow the whole column. -->
        <div class="cal-allday-label">all day</div>
        <div
          v-for="(col, i) in columns"
          :key="`a${col.day.startMs}`"
          class="cal-allday"
          :class="{ today: isToday(col.day.startMs), weekend: isWeekend(col.day.startMs), first: i === 0 }"
        >
          <button
            v-for="entry in col.allDay"
            :key="entry.id"
            type="button"
            class="cal-block cal-chip"
            :class="entry.kind"
            :style="{ '--cal-color': board.userColor(entry.userId) }"
            :title="`${summary(entry)}\n${spanLabel(entry.startMs, entry.endMs)}`"
            @click="openExisting(entry)"
          ><ClaudeMark v-if="entry.needsClaude" :size="10" />{{ board.userName(entry.userId) }}<span v-if="entry.note"> · {{ entry.note }}</span></button>
        </div>

        <div class="cal-gutter">
          <div v-for="h in HOURS" :key="h" class="cal-hour"><span v-if="h">{{ hourLabel(h) }}</span></div>
        </div>

        <div
          v-for="col in columns"
          :key="col.day.startMs"
          class="cal-col"
          :class="{ today: isToday(col.day.startMs), weekend: isWeekend(col.day.startMs) }"
          @pointerdown="startDrag($event, col.day)"
        >
          <button
            v-for="block in col.blocks"
            :key="block.item.entry.id"
            type="button"
            class="cal-block"
            :class="[
              block.item.entry.kind,
              { mine: block.item.entry.userId === board.me?.id, 'needs-claude': block.item.entry.needsClaude },
            ]"
            :style="boxStyle(block, col.day)"
            :title="`${summary(block.item.entry)}\n${spanLabel(block.item.entry.startMs, block.item.entry.endMs)}`"
            @click.stop="openExisting(block.item.entry)"
          >
            <span class="cal-block-who">
              <ClaudeMark v-if="block.item.entry.needsClaude" :size="10" />
              {{ board.userName(block.item.entry.userId) }}
            </span>
            <span class="cal-block-kind">{{ AVAILABILITY_LABELS[block.item.entry.kind].label }}</span>
            <span v-if="block.item.entry.note" class="cal-block-note">{{ block.item.entry.note }}</span>
          </button>

          <!-- After the blocks, so a line that crosses one is drawn over it rather
               than hidden behind it. Untouchable, so the block underneath still
               takes the click. -->
          <div v-for="line in col.primes" :key="`p${line.key}`" class="cal-prime" :style="line.style"></div>
          <div v-if="nowLine(col.day)" class="cal-now" :style="nowLine(col.day)!"></div>

          <div v-if="draft && draft.day.startMs === col.day.startMs" class="cal-draft" :style="draftStyle ?? {}">
            {{ timeLabel(draft.startMs) }}–{{ timeLabel(draft.endMs) }}
          </div>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="editing" class="modal-backdrop" @pointerdown.self="editing = null" @keydown="onKeydown">
        <form class="modal" role="dialog" aria-modal="true" aria-labelledby="cal-modal-title" @submit.prevent="save">
          <header class="modal-head">
            <span id="cal-modal-title">{{ editing.id ? "Edit" : "Add" }} availability</span>
            <span style="flex: 1"></span>
            <button class="btn ghost" type="button" title="Close" aria-label="Close" @click="editing = null">✕</button>
          </header>

          <div class="modal-body">
            <p v-if="readOnly" class="modal-lede">
              This is {{ board.userName(editing.userId) }}'s entry. Only they — or an admin — can change it.
            </p>

            <div class="fields modal-fields">
              <label>Whose</label>
              <select v-if="board.me?.isAdmin && !editing.id" v-model="editing.userId">
                <option v-for="p in people" :key="p.id" :value="p.id">{{ p.displayName }}</option>
              </select>
              <div v-else class="cal-owner">
                <span class="avatar" :style="{ background: board.userColor(editing.userId) }">
                  {{ board.userName(editing.userId).slice(0, 2) }}
                </span>
                {{ board.userName(editing.userId) }}
              </div>

              <label>What</label>
              <div class="cal-kinds">
                <label v-for="kind in AVAILABILITY_KINDS" :key="kind" class="cal-kind">
                  <input
                    type="radio"
                    :value="kind"
                    :checked="editing.kind === kind"
                    :disabled="readOnly"
                    @change="pickKind(kind)"
                  />
                  <span class="cal-swatch" :class="kind"></span>
                  {{ AVAILABILITY_LABELS[kind].label }}
                  <em>{{ AVAILABILITY_LABELS[kind].hint }}</em>
                </label>
              </div>

              <label>From</label>
              <input v-model="editing.from" type="datetime-local" step="3600" :disabled="readOnly" />

              <label>To</label>
              <div class="field-stack">
                <input v-model="editing.to" type="datetime-local" step="3600" :disabled="readOnly" />
                <span class="field-hint">Exclusive, and snapped down to the hour — like a task's schedule.</span>
              </div>

              <label>Note</label>
              <input
                v-model="editing.note"
                type="text"
                :maxlength="AVAILABILITY_NOTE_MAX"
                :disabled="readOnly"
                placeholder="Optional — “at the offsite”, “ship the parser fix”"
              />

              <label>Claude</label>
              <div class="field-stack">
                <label class="cal-kind cal-need-check">
                  <input
                    v-model="editing.needsClaude"
                    type="checkbox"
                    :disabled="readOnly || editing.kind === 'free'"
                  />
                  <ClaudeMark :size="12" />
                  Need Claude
                </label>
                <span class="field-hint">
                  {{ editing.kind === "free"
                    ? "A free span does not need Claude — you are around."
                    : "Flags the span on everyone's calendar. Say what needs doing in the note." }}
                </span>
              </div>
            </div>
          </div>

          <footer class="modal-foot">
            <button v-if="editing.id && !readOnly" class="btn ghost danger" type="button" :disabled="saving" @click="remove">
              Remove
            </button>
            <span v-if="serverError ?? blocker" class="field-problem modal-error">{{ serverError ?? blocker }}</span>
            <span style="flex: 1"></span>
            <button class="btn ghost" type="button" @click="editing = null">{{ readOnly ? "Close" : "Cancel" }}</button>
            <button v-if="!readOnly" class="btn primary" type="submit" :disabled="saving || !!blocker" title="⌘/Ctrl+Enter">
              {{ saving ? "Saving…" : "Save" }}
            </button>
          </footer>
        </form>
      </div>
    </Teleport>
  </section>
</template>
